import React, { useContext, useEffect } from "react";
import Row from "components/ui/layout/row/row";
import Column from "components/ui/layout/column/column";
import CardBox from "components/ui/controls/card-box/card-box";
import { AppContext } from "components/app/RemExprertProvider";

// Composant pour un graphique en barres verticales modernes
const ModernBarChart = ({ title, data, colors }) => {
  const maxValue = Math.max(...data.map((item) => item.value));

  return (
    <div
      style={{
        padding: "20px",
        backgroundColor: "#ffffff",
        borderRadius: "12px",
        boxShadow: "0 4px 6px rgba(0, 0, 0, 0.05)",
        border: "1px solid #e5e7eb",
        marginBottom: "20px",
        height: "400px",
      }}
    >
      <h3
        style={{
          fontSize: "14px",
          fontWeight: "600",
          color: "#1f2937",
          marginBottom: "20px",
          textAlign: "center",
        }}
      >
        {title}
      </h3>

      <div
        style={{
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-around",
          height: "350px",
          gap: "10px",
          paddingBottom: "40px",
        }}
      >
        {data.map((item, index) => (
          <div
            key={index}
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              flex: 1,
              maxWidth: "80px",
            }}
          >
            {/* Valeur au-dessus de la barre */}
            <div
              style={{
                fontSize: "12px",
                fontWeight: "600",
                color: "#374151",
                marginBottom: "8px",
                textAlign: "center",
                minHeight: "20px",
              }}
            >
              {`${item.value.toLocaleString("fr-FR")} €`}
            </div>
            {/* Barre verticale */}
            <div
              style={{
                width: "70px",
                height: `${maxValue > 0 ? (item.value / maxValue) * 200 : 2}px`,
                background: `linear-gradient(135deg, ${colors[index] || "#3b82f6"} 0%, ${colors[index] || "#3b82f6"}cc 50%, ${colors[index] || "#3b82f6"}99 100%)`,
                borderRadius: "8px 8px 4px 4px",
                transition: "height 0.8s ease-in-out, transform 0.2s ease",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                position: "relative",
                cursor: "pointer",
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = "scale(1.05)";
                e.target.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.15)";
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = "scale(1)";
                e.target.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.1)";
              }}
            ></div>

            {/* Label sous la barre */}
            <div
              style={{
                fontSize: "12px",
                fontWeight: "500",
                color: "#6b7280",
                textAlign: "center",
                marginTop: "8px",
                lineHeight: "1.2",
                height: "32px",
                display: "flex",
                alignItems: "center",
              }}
            >
              {item.label}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Composant pour la légende moderne
const ModernLegend = ({ items }) => {
  return (
    <div
      style={{
        padding: "20px",
        backgroundColor: "#ffffff",
        borderRadius: "12px",
        boxShadow: "0 4px 6px rgba(0, 0, 0, 0.05)",
        border: "1px solid #e5e7eb",
        marginBottom: "20px",
        height: "400px",
        display: "flex",
        flexDirection: "column",
        boxSizing: "border-box",
      }}
    >
      <h4
        style={{
          fontSize: "14px",
          fontWeight: "600",
          color: "#1f2937",
          marginBottom: "20px",
          textAlign: "center",
          margin: "0 0 20px 0",
        }}
      >
        Légende
      </h4>

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "16px",
          flex: 1,
          justifyContent: "center",
        }}
      >
        {items.map((item, index) => (
          <div
            key={index}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              padding: "12px",
              borderRadius: "8px",
              backgroundColor: "#f9fafb",
              transition: "all 0.2s ease",
            }}
          >
            <div
              style={{
                width: "20px",
                height: "20px",
                borderRadius: "4px",
                backgroundColor: item.color,
                boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
              }}
            ></div>
            <span
              style={{
                fontSize: "12px",
                fontWeight: "500",
                color: "#374151",
              }}
            >
              {item.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

const DetailDisponibleTNS = ({ InfosDisponible, InfosPerp, GraphDisponible, GraphPerp, ClientConjoint }) => {
  const context = useContext(AppContext);

  useEffect(() => {
    if (GraphDisponible !== undefined) {
      document.getElementById("GraphDispoRetraiteMadelin").innerHTML = GraphDisponible?.GraphMadelin?.GraphDispoRetraite;
      document.getElementById("GraphDispoPrevSanteMadelin").innerHTML = GraphDisponible?.GraphMadelin?.GraphDispoPrevSante;
    }
    if (GraphPerp !== undefined) {
      document.getElementById("GraphDispoPerpTNS").innerHTML = GraphPerp;
    }
  }, [GraphDisponible, GraphPerp]);


  // Extraction des vraies données depuis les props
  const retraiteData = [
    { label: "Disponible fiscal", value: InfosDisponible.PlafondRetraite },
    { label: "Versements effectués", value: InfosDisponible.CotisationRetraite },
    { label: "Disponible restant", value: InfosDisponible.DispoRestantRetraite },
    { label: "Dépassement", value: InfosDisponible.DepassementRetraite },
  ];

  const prevoyanceData = [
    { label: "Disponible fiscal", value: InfosDisponible.PlafondPrevoyanceSante },
    { label: "Versements effectués", value: InfosDisponible.CotisationPrevSante },
    { label: "Disponible restant", value: InfosDisponible.DispoRestantPrevoyanceSante },
    { label: "Dépassement", value: InfosDisponible.DepassementPrevoyanceSante },
  ];

  const perpData = [
    { label: "Disponible fiscal", value: ClientConjoint === "" ? Math.round(InfosPerp?.PlafondPerpReelDirigeant) : Math.round(InfosPerp?.PlafondPerpReelConjoint, 0) },
    { label: "Versements effectués", value: ClientConjoint === "" ? Math.round(InfosPerp?.VersementPerp) : Math.round(InfosPerp?.VersementPerp, 0) },
    { label: "Disponible restant", value:  ClientConjoint === "" ? Math.round(InfosPerp?.DispoRestantPerp) : Math.round(InfosPerp?.DispoRestantPerpConjoint, 0) },
    { label: "Dépassement", value: ClientConjoint === "" ? Math.round(InfosPerp?.DepassementPerp) : Math.round(InfosPerp?.DepassementPerpConjoint, 0)  },
  ];

  const legendItems = [
    { label: "Disponible fiscal", color: context.state.Color1 || "#3b82f6" },
    { label: "Dépassement", color: "#405060" },
    { label: "Versements effectués", color: context.state.Color2 || "#10b981" },
    { label: "Disponible restant", color: "#d3d3d3" },
  ];

  const chartColors = [
    context.state.Color1 || "#3b82f6", // Disponible fiscal
    context.state.Color2 || "#10b981", // Versements effectués
    "#d3d3d3", // Disponible restant
    "#405060", // Dépassement
  ];
  console.log("GraphDisponible", GraphDisponible);
  return (
    <Column fill>
      <CardBox>
        <Column fill>
          <Row>
            <div
              style={{
                width: "100%",
                textAlign: "center",
                padding: "13px 0",
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                borderRadius: "12px",
                marginBottom: "30px",
              }}
            >
              <h1
                style={{
                  fontSize: "14px",
                  fontWeight: "700",
                  color: "#ffffff",
                  margin: "0",
                  textShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                }}
              >
                Disponibles fiscaux
              </h1>
            </div>
          </Row>

          {/* Première ligne : Retraite Madelin et Prévoyance */}
          <Row style={{ gap: "20px", alignItems: "flex-start", marginBottom: "20px" }}>
            <Column style={{ flex: 1 }}>
              <div style={{ position: "relative" }}>
                <ModernBarChart title="Retraite Madelin" data={retraiteData} colors={chartColors} />
                {/* Container caché pour les données réelles */}
                <div id="GraphDispoRetraiteMadelin" style={{ display: "none" }}></div>
              </div>
            </Column>

            <Column style={{ flex: 1 }}>
              <div style={{ position: "relative" }}>
                <ModernBarChart title="Prévoyance / Santé Madelin" data={prevoyanceData} colors={chartColors} />
                {/* Container caché pour les données réelles */}
                <div id="GraphDispoPrevSanteMadelin" style={{ display: "none" }}></div>
              </div>
            </Column>
          </Row>

          {/* Deuxième ligne : PERP / PER et Légende */}
          <Row style={{ gap: "20px", alignItems: "flex-start" }}>
            <Column style={{ flex: 1 }}>
              <div style={{ position: "relative" }}>
                <ModernBarChart title="PERP / PER" data={perpData} colors={chartColors} />
                {/* Container caché pour les données réelles */}
                <div id="GraphDispoPerpTNS" style={{ display: "none" }}></div>
              </div>
            </Column>

            <Column style={{ flex: 1 }}>
              <ModernLegend items={legendItems} />
            </Column>
          </Row>
        </Column>
      </CardBox>
    </Column>
  );
};

export default DetailDisponibleTNS;
